import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiPlay, FiPause, FiSkipBack, FiSkipForward, FiVolume2, FiVolumeX,
  FiRepeat, FiShuffle, FiList, FiChevronUp, FiChevronDown, FiMusic,
} from 'react-icons/fi';
import { useMusicPlayer } from './MusicPlayerProvider';

function formatTime(seconds) {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default function MusicPlayer() {
  const {
    currentTrack,
    queue,
    isPlaying,
    progress,
    duration,
    volume,
    isMuted,
    repeatMode,
    isShuffled,
    isLoading,
    togglePlayPause,
    playNext,
    playPrevious,
    seek,
    seekPercent,
    setVolumeLevel,
    toggleMute,
    toggleRepeat,
    toggleShuffle,
  } = useMusicPlayer();

  const [isExpanded, setIsExpanded] = useState(false);
  const [showQueue, setShowQueue] = useState(false);
  // Local seek state so the thumb doesn't jump while dragging
  const [isSeeking, setIsSeeking] = useState(false);
  const [seekValue, setSeekValue] = useState(0);

  if (!currentTrack) return null;

  const displayProgress = isSeeking ? seekValue : progress;
  const progressPercent = duration ? (displayProgress / duration) * 100 : 0;

  const handleSeekStart = (e) => {
    setIsSeeking(true);
    setSeekValue(parseFloat(e.target.value));
  };

  const handleSeekChange = (e) => {
    setSeekValue(parseFloat(e.target.value));
  };

  const handleSeekEnd = (e) => {
    seek(parseFloat(e.target.value));
    setIsSeeking(false);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        exit={{ y: 100 }}
        className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50"
      >
        <div className="container mx-auto px-4 py-2">
          {/* Desktop: 3-column layout */}
          <div className="hidden md:grid md:grid-cols-3 md:items-center md:gap-4">

            {/* Left: Track info */}
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-11 h-11 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                {currentTrack.album_artwork ? (
                  <img
                    src={currentTrack.album_artwork}
                    alt={currentTrack.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <FiMusic className="w-5 h-5 text-gray-400" />
                )}
              </div>
              <div className="min-w-0">
                <p className="font-medium text-gray-900 truncate text-sm">{currentTrack.title}</p>
                <p className="text-xs text-gray-500 truncate">
                  {currentTrack.artist || currentTrack.album_title || 'Unknown'}
                </p>
              </div>
            </div>

            {/* Center: Controls + Seek */}
            <div className="flex flex-col items-center gap-1.5">
              {/* Playback controls */}
              <div className="flex items-center gap-1">
                <button
                  onClick={toggleShuffle}
                  className={`p-2 rounded-full hover:bg-gray-100 ${isShuffled ? 'text-primary' : 'text-gray-400'}`}
                  title="Shuffle"
                >
                  <FiShuffle className="w-4 h-4" />
                </button>
                <button
                  onClick={playPrevious}
                  className="p-2 rounded-full hover:bg-gray-100 text-gray-700"
                  title="Previous"
                >
                  <FiSkipBack className="w-5 h-5" />
                </button>
                <button
                  onClick={togglePlayPause}
                  disabled={isLoading}
                  className="p-2.5 bg-primary text-white rounded-full hover:bg-primary/90 disabled:opacity-50 mx-1"
                  title={isPlaying ? 'Pause' : 'Play'}
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : isPlaying ? (
                    <FiPause className="w-5 h-5" />
                  ) : (
                    <FiPlay className="w-5 h-5 ml-0.5" />
                  )}
                </button>
                <button
                  onClick={playNext}
                  className="p-2 rounded-full hover:bg-gray-100 text-gray-700"
                  title="Next"
                >
                  <FiSkipForward className="w-5 h-5" />
                </button>
                <button
                  onClick={toggleRepeat}
                  className={`p-2 rounded-full hover:bg-gray-100 relative ${repeatMode !== 'off' ? 'text-primary' : 'text-gray-400'}`}
                  title={`Repeat: ${repeatMode}`}
                >
                  <FiRepeat className="w-4 h-4" />
                  {repeatMode === 'one' && (
                    <span className="absolute -top-0.5 -right-0.5 text-[9px] font-bold leading-none">1</span>
                  )}
                </button>
              </div>

              {/* Seek bar */}
              <div className="flex items-center gap-2 w-full">
                <span className="text-xs text-gray-400 tabular-nums w-8 text-right flex-shrink-0">
                  {formatTime(displayProgress)}
                </span>
                <div className="relative flex-1 flex items-center group">
                  <input
                    type="range"
                    min="0"
                    max={duration || 0}
                    step="0.5"
                    value={displayProgress}
                    onMouseDown={handleSeekStart}
                    onChange={handleSeekChange}
                    onMouseUp={handleSeekEnd}
                    onTouchStart={handleSeekStart}
                    onTouchEnd={handleSeekEnd}
                    className="w-full h-1 bg-gray-200 rounded-full appearance-none cursor-pointer accent-primary
                               [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
                               [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary
                               [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:opacity-0
                               group-hover:[&::-webkit-slider-thumb]:opacity-100
                               [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3
                               [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary
                               [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:opacity-0
                               group-hover:[&::-moz-range-thumb]:opacity-100"
                    style={{
                      background: `linear-gradient(to right, var(--color-primary, #7c3aed) ${progressPercent}%, #e5e7eb ${progressPercent}%)`,
                    }}
                  />
                </div>
                <span className="text-xs text-gray-400 tabular-nums w-8 flex-shrink-0">
                  {formatTime(duration)}
                </span>
              </div>
            </div>

            {/* Right: Volume + Queue */}
            <div className="flex items-center gap-2 justify-end">
              <div className="flex items-center gap-1.5">
                <button
                  onClick={toggleMute}
                  className="p-2 rounded-full hover:bg-gray-100 text-gray-500"
                >
                  {isMuted || volume === 0 ? (
                    <FiVolumeX className="w-4 h-4" />
                  ) : (
                    <FiVolume2 className="w-4 h-4" />
                  )}
                </button>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={isMuted ? 0 : volume * 100}
                  onChange={(e) => setVolumeLevel(parseInt(e.target.value) / 100)}
                  className="w-20 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                />
              </div>

              <button
                onClick={() => setShowQueue(!showQueue)}
                className={`relative p-2 rounded-full hover:bg-gray-100 ${showQueue ? 'text-primary' : 'text-gray-500'}`}
                title="Queue"
              >
                <FiList className="w-4 h-4" />
                {queue.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-primary text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                    {queue.length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Mobile: compact row */}
          <div className="flex md:hidden items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
              {currentTrack.album_artwork ? (
                <img src={currentTrack.album_artwork} alt={currentTrack.title} className="w-full h-full object-cover" />
              ) : (
                <FiMusic className="w-4 h-4 text-gray-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 truncate text-sm">{currentTrack.title}</p>
              <p className="text-xs text-gray-500 truncate">{currentTrack.artist || currentTrack.album_title || 'Unknown'}</p>
            </div>
            <button
              onClick={playPrevious}
              className="p-2 rounded-full hover:bg-gray-100 text-gray-700"
            >
              <FiSkipBack className="w-5 h-5" />
            </button>
            <button
              onClick={togglePlayPause}
              disabled={isLoading}
              className="p-2.5 bg-primary text-white rounded-full hover:bg-primary/90 disabled:opacity-50"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : isPlaying ? (
                <FiPause className="w-5 h-5" />
              ) : (
                <FiPlay className="w-5 h-5 ml-0.5" />
              )}
            </button>
            <button
              onClick={playNext}
              className="p-2 rounded-full hover:bg-gray-100 text-gray-700"
            >
              <FiSkipForward className="w-5 h-5" />
            </button>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 rounded-full hover:bg-gray-100 text-gray-500"
            >
              {isExpanded ? <FiChevronDown className="w-5 h-5" /> : <FiChevronUp className="w-5 h-5" />}
            </button>
          </div>

          {/* Mobile: seek bar (always visible) */}
          <div className="flex md:hidden items-center gap-2 mt-1.5">
            <span className="text-xs text-gray-400 tabular-nums w-8 text-right flex-shrink-0">
              {formatTime(displayProgress)}
            </span>
            <div className="relative flex-1 flex items-center group">
              <input
                type="range"
                min="0"
                max={duration || 0}
                step="0.5"
                value={displayProgress}
                onMouseDown={handleSeekStart}
                onChange={handleSeekChange}
                onMouseUp={handleSeekEnd}
                onTouchStart={handleSeekStart}
                onTouchEnd={handleSeekEnd}
                className="w-full h-1 bg-gray-200 rounded-full appearance-none cursor-pointer accent-primary
                           [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
                           [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary
                           [&::-webkit-slider-thumb]:appearance-none
                           [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3
                           [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary
                           [&::-moz-range-thumb]:border-0"
                style={{
                  background: `linear-gradient(to right, var(--color-primary, #7c3aed) ${progressPercent}%, #e5e7eb ${progressPercent}%)`,
                }}
              />
            </div>
            <span className="text-xs text-gray-400 tabular-nums w-8 flex-shrink-0">
              {formatTime(duration)}
            </span>
          </div>
        </div>

        {/* Expanded view (mobile) / Queue */}
        <AnimatePresence>
          {(isExpanded || showQueue) && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="border-t border-gray-100 overflow-hidden"
            >
              <div className="container mx-auto px-4 py-4 max-h-64 overflow-y-auto">
                {showQueue ? (
                  <QueueList />
                ) : (
                  <div className="md:hidden space-y-3">
                    {/* Mobile extra controls */}
                    <div className="flex items-center justify-center gap-6">
                      <button
                        onClick={toggleShuffle}
                        className={`p-2 ${isShuffled ? 'text-primary' : 'text-gray-500'}`}
                      >
                        <FiShuffle className="w-5 h-5" />
                      </button>
                      <button
                        onClick={toggleRepeat}
                        className={`p-2 relative ${repeatMode !== 'off' ? 'text-primary' : 'text-gray-500'}`}
                      >
                        <FiRepeat className="w-5 h-5" />
                        {repeatMode === 'one' && (
                          <span className="absolute -top-1 -right-1 text-xs font-bold">1</span>
                        )}
                      </button>
                      <button
                        onClick={() => setShowQueue(true)}
                        className="p-2 text-gray-500"
                      >
                        <FiList className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Mobile volume */}
                    <div className="flex items-center gap-3">
                      <FiVolumeX className="w-4 h-4 text-gray-400" />
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={isMuted ? 0 : volume * 100}
                        onChange={(e) => setVolumeLevel(parseInt(e.target.value) / 100)}
                        className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                      />
                      <FiVolume2 className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
}

function QueueList() {
  const { queue, queueIndex, removeFromQueue, playTrackAtIndex } = useMusicPlayer();

  if (queue.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <FiList className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>Queue is empty</p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <h3 className="font-medium text-gray-900 mb-2">Up Next ({queue.length})</h3>
      {queue.map((track, index) => (
        <div
          key={`${track.id}-${index}`}
          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-gray-50 ${
            index === queueIndex ? 'bg-primary/5' : ''
          }`}
          onClick={() => playTrackAtIndex(index)}
        >
          <span className="text-sm text-gray-400 w-6 text-center">
            {index === queueIndex ? (
              <FiPlay className="w-4 h-4 text-primary" />
            ) : (
              index + 1
            )}
          </span>
          <div className="flex-1 min-w-0">
            <p className={`truncate ${index === queueIndex ? 'font-medium text-primary' : 'text-gray-900'}`}>
              {track.title}
            </p>
            <p className="text-sm text-gray-500 truncate">{track.artist}</p>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              removeFromQueue(index);
            }}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            &times;
          </button>
        </div>
      ))}
    </div>
  );
}
